import React from 'react'
import { Transaction } from '../../../../types/donation/transaction'
import { TextField } from '../../fields/TextField'
import { FieldBlock } from '../../fields/FieldBlock'
import { paymentMethodTypeStrings, transactionStatusStrings } from '../../../../utils/strings/DonationStrings'
import CopyableLabel from '../../common/CopyableLabel'
import { TransactionStatus } from '../../../../types/donation/transaction-status'
import { translations } from '../../../../utils/translations/translations-manager'
import { formatAmount } from '../../../../utils/strings/amount-format'

const transactionFields: Record<string, { getLabel: () => string; getValue: (transaction: Transaction) => string | undefined }> = {
  status: {
    getLabel: () => translations.texts.status.toString(),
    getValue: transaction => (transaction.transactionStatus ? transactionStatusStrings[transaction.transactionStatus]() : undefined),
  },
  payment_method: {
    getLabel: () => translations.texts.payment_method.toString(),
    getValue: transaction => (transaction.paymentMethodType ? paymentMethodTypeStrings[transaction.paymentMethodType]() : undefined),
  },
  amount: {
    getLabel: () => translations.texts.amount.toString(),
    getValue: transaction => formatAmount((transaction.donationAmount ?? transaction.selectedDonationAmount ?? 0) / 100),
  },
  creation_data: {
    getLabel: () => translations.texts.creation_date.toString(),
    getValue: transaction => (transaction.createdAt ? new Date(transaction.createdAt)?.toLocaleString(translations.getLanguage()) : undefined),
  },
  last_four: { getLabel: () => translations.texts.short_payment_method_last_four.toString(), getValue: transaction => transaction.stripePaymentMethodLast4 },
  stripe_id: { getLabel: () => translations.texts.stripe_payment_id.toString(), getValue: transaction => transaction.stripePaymentIntentId },
  stripe_destination_payment: {
    getLabel: () => translations.texts.stripe_destination_payment.toString(),
    getValue: transaction => transaction.stripeDestinationPayment,
  },
  refunded_amount: {
    getLabel: () => translations.texts.refunded_amount.toString(),
    getValue: transaction => formatAmount((transaction.refundedAmount ?? 0) / 100),
  },
}

// TODO: Add Location
const TransactionView: React.FC<{ transaction: Transaction }> = ({ transaction }) => {
  return (
    <div className="transaction-view flex flex-col gap-3 p-5 bg-cents-white rounded-2xl">
      <div className="transaction-header flex flex-col md:flex-row gap-3 md:items-center">
        <div className="bg-cents-white rounded-2xl flex items-center justify-start gap-3 flex-wrap">
          {transaction && transaction.transactionStatus && (
            <div
              className={`h-10 w-fit px-8 flex items-center font-bold text-sm rounded-2xl 
              ${
                [TransactionStatus.failed, TransactionStatus.disputed].includes(transaction.transactionStatus)
                  ? 'bg-red-100 text-red-800'
                  : [TransactionStatus.pending, TransactionStatus.draft, TransactionStatus.payment_request, TransactionStatus.pre_transaction].includes(
                        transaction.transactionStatus
                      )
                    ? 'bg-yellow-100 text-yellow-800'
                    : [TransactionStatus.paid].includes(transaction.transactionStatus)
                      ? 'bg-green-100 text-green-800'
                      : [TransactionStatus.refunded].includes(transaction.transactionStatus)
                        ? 'bg-blue-100 text-blue-800'
                        : 'text-cents-black bg-cents-grey'
              }`}
            >
              {transactionFields['status'].getValue(transaction)}
            </div>
          )}
          <p className=" text-cents-purple text-xl lg:text-[22px] font-semibold">{translations.texts.transaction.toString()}</p>
          {transaction?._id && <CopyableLabel prefix="ID: " text={transaction._id} className="relative top-0.5" />}
        </div>
      </div>
      <div className="transaction-fields h-fit flex flex-wrap gap-3 md:gap-y-5 md:gap-x-10">
        {Object.entries(transactionFields).map(([label, value]) => (
          <FieldBlock key={label} label={value.getLabel()} className="whitespace-nowrap max-w-[350px]">
            <TextField value={value.getValue(transaction) ?? ''} />
          </FieldBlock>
        ))}
      </div>
    </div>
  )
}
export default TransactionView
